import { apiFetch, buildQueryString } from '@/lib/api'
import type { components } from '@/types/api'

/** İK paneli backend uçları (`/api/v1/hr/*`, docs/IK-PANEL-PLAN.md). Listeler Cube'dan gelir. */
export type HrScope = components['schemas']['ScopeRead']
export type HrAction = components['schemas']['ActionRead']
export type HrActionCreate = components['schemas']['ActionCreate']
export type HrHistory = components['schemas']['HistoryRead']
export type HrHistoryEvent = components['schemas']['HistoryEvent']
export type HrProcesses = components['schemas']['ProcessesRead']
export type HrQueue = components['schemas']['QueueRead']
export type HrProcess = components['schemas']['ProcessRead']
export type HrFlowRun = components['schemas']['FlowRunRead']
export type HrRequest = components['schemas']['RequestRead']
export type HrRequestDetail = components['schemas']['RequestDetail']
export type HrRequestCreate = components['schemas']['RequestCreate']
export type HrRequestResubmit = components['schemas']['RequestResubmit']
export type HrRequestStatus = HrRequest['status']
export type HrRequestCounts = components['schemas']['RequestCounts']
export type HrRequestCheck = components['schemas']['RequestCheck']
export type HrRequestCheckRead = components['schemas']['RequestCheckRead']
export type HrBulkApproveItem = components['schemas']['BulkApproveItem']
export type HrIntervention = components['schemas']['InterventionRead']
export type HrTask = components['schemas']['TaskRead']
export type HrRecordKind = HrActionCreate['target_kind']
export type HrHistoryKind = HrHistory['kind']

export const hrApi = {
  scope: () => apiFetch<HrScope>('/hr/scope'),

  createAction: (body: HrActionCreate) =>
    apiFetch<HrAction>('/hr/actions', { method: 'POST', body }),

  /** Kuyrukta bekleyen (akışın henüz almadığı) kayıt aksiyonunu geri alır. */
  cancelAction: (id: string) => apiFetch<HrAction>(`/hr/actions/${id}/cancel`, { method: 'POST' }),

  latestActions: (kind: HrRecordKind, ids: string[]) =>
    apiFetch<HrAction[]>(`/hr/actions${buildQueryString({ target_kind: kind, ids })}`),

  history: (kind: HrHistoryKind, id: string) =>
    apiFetch<HrHistory>(`/hr/records/${kind}/${encodeURIComponent(id)}/history`),

  interventions: () => apiFetch<HrIntervention[]>('/hr/interventions'),

  /** İşlerim: senkronun çözemediği kayıtlar, kullanıcının kapsamında. */
  tasks: () => apiFetch<HrTask[]>('/hr/tasks'),

  processes: () => apiFetch<HrProcesses>('/hr/processes'),

  /** Aksiyon kuyruğu: bekleyen sayısı ve en eski bekleme. */
  queue: () => apiFetch<HrQueue>('/hr/queue'),

  processRuns: (key: string) => apiFetch<HrFlowRun[]>(`/hr/processes/${key}/runs`),

  runProcess: (key: string) =>
    apiFetch<components['schemas']['ProcessRunRead']>(`/hr/processes/${key}/run`, {
      method: 'POST',
    }),

  requests: (status?: HrRequestStatus) =>
    apiFetch<HrRequest[]>(`/hr/requests${buildQueryString({ status })}`),

  request: (id: string) => apiFetch<HrRequestDetail>(`/hr/requests/${id}`),

  /** Onaylar sekme sayıları ve menü rozetleri. */
  requestCounts: () => apiFetch<HrRequestCounts>('/hr/requests/counts'),

  /** İzin güncelleme talebinde seçilebilecek türler (DİA'da karşılığı olanlar). */
  leaveTypes: () => apiFetch<string[]>('/hr/leave-types'),

  /** Seçilen talepleri toplu onaylar; her biri ayrı doğrulanır, sonuç talep başına. */
  bulkApprove: (ids: string[], message?: string) =>
    apiFetch<HrBulkApproveItem[]>('/hr/requests/bulk-approve', {
      method: 'POST',
      body: { ids, message: message?.trim() || null },
    }),

  /** Giriş/çıkış talebi formunun ön kontrolü: engelleyen sınırlar ve uyarılar. */
  checkRequest: (body: HrRequestCheck) =>
    apiFetch<HrRequestCheckRead>('/hr/requests/check', { method: 'POST', body }),

  createRequest: (body: HrRequestCreate) =>
    apiFetch<HrRequestDetail>('/hr/requests', { method: 'POST', body }),

  resubmitRequest: (id: string, body: HrRequestResubmit) =>
    apiFetch<HrRequestDetail>(`/hr/requests/${id}`, { method: 'PUT', body }),

  decideRequest: (
    id: string,
    decision: 'approve' | 'revise' | 'cancel' | 'retry' | 'resolve' | 'withdraw',
    message?: string,
  ) =>
    apiFetch<HrRequestDetail>(`/hr/requests/${id}/${decision}`, {
      method: 'POST',
      body: { message: message?.trim() || null },
    }),
}
